import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, Check, X, Loader2, Users, Phone, Upload } from 'lucide-react';
import { getPlanById } from '@/data/pricingPlans';
import { billingApi } from '@/lib/api';

interface UltimateOwlUpsellModalProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
}

const ultimateFeatures = [
  {
    icon: Upload,
    title: 'White glove data migration',
    description: 'We\'ll import your spreadsheets, clean up duplicates, and set you up right.',
  },
  {
    icon: Users,
    title: 'Dedicated onboarding specialist',
    description: 'A real person to walk you through everything, not a chatbot.',
  },
  {
    icon: Phone,
    title: 'Priority phone support',
    description: 'Skip the queue. Get help when you need it most.',
  },
];

const animationConfig = {
  backdrop: { bg: 'rgba(0, 0, 0, 0.04)', blur: '8px' },
  duration: 400,
};

export function UltimateOwlUpsellModal({ open, onClose, businessId }: UltimateOwlUpsellModalProps) {
  const [interval, setInterval] = useState<'monthly' | 'annual'>('annual');
  const [isLoading, setIsLoading] = useState(false);
  const [phase, setPhase] = useState<'entering' | 'visible' | 'exiting' | 'hidden'>('hidden');

  const plan = getPlanById('unlimited_owl');
  const config = animationConfig;

  useEffect(() => {
    if (open) {
      setPhase('entering');
      const timer = setTimeout(() => setPhase('visible'), 50);
      return () => clearTimeout(timer);
    } else {
      setPhase('hidden');
    }
  }, [open]);

  const handleClose = useCallback(() => {
    setPhase('exiting');
    setTimeout(() => {
      onClose();
    }, config.duration);
  }, [onClose, config.duration]);

  if (!plan) return null;

  // All pricing from pricingPlans.ts
  const monthlyPrice = plan.pricing.monthly;
  const annualPrice = plan.pricing.annually;
  const price = interval === 'annual' ? annualPrice : monthlyPrice;
  const savingsPercent = monthlyPrice > 0 ? Math.round((1 - annualPrice / monthlyPrice) * 100) : 0;

  const showContent = phase !== 'hidden';
  if (!showContent && phase === 'hidden') return null;

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const response = await billingApi.createCheckoutSession('unlimited_owl', interval, businessId);
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        onClick={handleClose}
        className="absolute inset-0"
        style={{
          backgroundColor: phase === 'visible' ? config.backdrop.bg : 'rgba(0, 0, 0, 0)',
          backdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          WebkitBackdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          transition: `background-color ${config.duration}ms ease-out, backdrop-filter ${config.duration}ms ease-out`,
        }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-3xl"
        style={{
          opacity: phase === 'visible' ? 1 : 0,
          transform: phase === 'visible' ? 'scale(1)' : 'scale(0.98)',
          transition: `opacity ${config.duration}ms ease-out, transform ${config.duration}ms ease-out`,
        }}
      >
        <div className="relative bg-white dark:bg-zinc-900 rounded-[32px] shadow-2xl overflow-hidden">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="grid md:grid-cols-2">
            {/* Left side - Features */}
            <div className="p-8 md:p-10 bg-zinc-50 dark:bg-zinc-800/50">
              <div className="mb-8">
                <div className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">
                  <Sparkles className="h-4 w-4" />
                  Premium Service
                </div>
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                  Let us handle the setup
                </h2>
              </div>

              <p className="text-zinc-600 dark:text-zinc-400 mb-6">
                Moving from spreadsheets or another tool? We'll do the heavy lifting so you can focus on your business.
              </p>

              <div className="space-y-5">
                {ultimateFeatures.map((feature) => (
                  <div key={feature.title} className="flex gap-4">
                    <div className="h-10 w-10 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-5 w-5 text-zinc-700 dark:text-zinc-300" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right side - Pricing */}
            <div className="p-8 md:p-10 flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
                  {plan.name}
                </h3>

                {/* Billing toggle */}
                <Tabs value={interval} onValueChange={(v) => setInterval(v as 'monthly' | 'annual')}>
                  <TabsList className="h-10 w-full">
                    <TabsTrigger value="monthly" className="flex-1">Monthly</TabsTrigger>
                    <TabsTrigger value="annual" className="flex-1">
                      Annual
                      <Badge variant="success" className="ml-1.5 text-[10px] px-1.5 py-0">
                        SAVE {savingsPercent}%
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                    ${price}
                  </span>
                  <span className="text-zinc-500 dark:text-zinc-400 ml-1">/mo</span>
                </div>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  {interval === 'annual' ? `$${price * 12} billed annually` : 'billed monthly'}
                </p>
              </div>

              {/* Plan features from pricingPlans.ts */}
              <div className="mb-6 flex-1">
                <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-3">
                  Everything included
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {plan.features.slice(0, 6).map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-auto pt-6">
                <Button
                  onClick={handleUpgrade}
                  disabled={isLoading}
                  className="w-full h-11"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    `Upgrade to ${plan.name}`
                  )}
                </Button>

                <button
                  onClick={handleClose}
                  className="w-full mt-1 py-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
